import {
    CalculatorPresentationContractError,
    requirePresentationArray,
    requirePresentationFields,
    requirePresentationNullableString,
    requirePresentationRecord,
    requirePresentationString,
} from "./calculator-presentation-contract.js";
import {
    calculatorProvenanceClaimEntries,
    calculatorProvenanceFactKindLabel,
    mergeCalculatorProvenanceClaims,
    projectCalculatorProvenanceCandidate,
} from "./calculator-provenance-candidate.js";
import {
    createCalculatorProvenanceFactPresenter,
} from "./calculator-provenance-fact-presentation.js";
import { calculatorDataText } from "./calculator-data-i18n.js";
import {
    provenanceClaimsText,
    typedProvenanceIndicatorStyle,
} from "./provenance-indicator.js";
import { provenanceIndicatorColorForTone } from "./provenance-tone.js";
import { calculatorLootSubjectFactChannel } from "./calculator-loot-subject.js";
import {
    canonicalizeCalculatorFishingSourceV1,
} from "./calculator-fishing-source.js";
import {
    calculatorItemQuantityHistoryIsPresentationRelevant,
} from "./calculator-item-quantity-presentation.js";

function requireRecord(value, path) {
    return requirePresentationRecord(value, path);
}

function requireString(record, key, path) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) {
        throw new CalculatorPresentationContractError(`${path}.${key}`, "missing field");
    }
    return requirePresentationString(record[key], `${path}.${key}`);
}

function requirePositiveInteger(value, path) {
    if (!Number.isInteger(value) || value <= 0) {
        throw new CalculatorPresentationContractError(path, "expected a positive integer");
    }
    return value;
}

function requireFishingSource(record, key, path) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) {
        throw new CalculatorPresentationContractError(`${path}.${key}`, "missing field");
    }
    return canonicalizeCalculatorFishingSourceV1(
        record[key],
        `${path}.${key}`,
        (issuePath, _code, message) => {
            throw new CalculatorPresentationContractError(issuePath, message);
        },
    );
}

function parseResolverPresentation(value) {
    const presentation = requireRecord(value, "presentation");
    const provenance = requirePresentationFields(
        presentation.provenance,
        ["evidenceClasses", "factKinds", "statuses"],
        "presentation.provenance",
    );
    const labels = requireRecord(presentation.labels, "presentation.labels");
    const conditionsByOperationId = Object.freeze(Object.fromEntries(
        Object.entries(requireRecord(
            presentation.conditionsByOperationId,
            "presentation.conditionsByOperationId",
        )).map(([operationId, conditionText]) => [
            requirePresentationString(
                operationId,
                "presentation.conditionsByOperationId key",
            ),
            requirePresentationString(
                conditionText,
                `presentation.conditionsByOperationId.${operationId}`,
            ),
        ]),
    ));
    return Object.freeze({
        conditionsByOperationId,
        factPresenter: createCalculatorProvenanceFactPresenter(presentation),
        provenance: Object.freeze({
            evidenceClasses: requireRecord(
                provenance.evidenceClasses,
                "presentation.provenance.evidenceClasses",
            ),
            factKinds: requireRecord(
                provenance.factKinds,
                "presentation.provenance.factKinds",
            ),
            statuses: requireRecord(
                provenance.statuses,
                "presentation.provenance.statuses",
            ),
        }),
        unknownValueLabel: requirePresentationString(
            labels.unknownValueLabel,
            "presentation.labels.unknownValueLabel",
        ),
    });
}

function provenanceTraceMap(value) {
    const traces = new Map();
    requirePresentationArray(value, "projection.provenanceTraces").forEach((trace, index) => {
        const path = `projection.provenanceTraces[${index}]`;
        const record = requireRecord(trace, path);
        const traceId = requireString(record, "traceId", path);
        if (traces.has(traceId)) {
            throw new CalculatorPresentationContractError(path, `duplicate traceId ${traceId}`);
        }
        traces.set(traceId, record);
    });
    return traces;
}

function rateAlternativeResolutionMap(value) {
    const resolutions = new Map();
    requirePresentationArray(
        value ?? [],
        "projection.rateAlternativeResolutions",
    ).forEach((entry, index) => {
        const path = `projection.rateAlternativeResolutions[${index}]`;
        const resolution = requireRecord(entry, path);
        const kind = requireString(resolution, "kind", path);
        const familyId = requireString(resolution, "familyId", path);
        if (resolutions.has(familyId)) {
            throw new CalculatorPresentationContractError(
                `${path}.familyId`,
                `duplicate rate-alternative family ${familyId}`,
            );
        }
        if (kind === "automatic" || kind === "override") {
            resolutions.set(familyId, Object.freeze({
                alternativeId: requireString(resolution, "alternativeId", path),
                kind,
            }));
            return;
        }
        if (kind === "inactive" || kind === "selectionRequired") {
            resolutions.set(familyId, Object.freeze({
                alternativeId: null,
                kind,
            }));
            return;
        }
        throw new CalculatorPresentationContractError(
            `${path}.kind`,
            "expected automatic, override, selectionRequired, or inactive",
        );
    });
    return resolutions;
}

function traceRateAlternative(trace, path) {
    let resolved = null;
    requirePresentationArray(trace.candidates, `${path}.candidates`)
        .forEach((candidate, candidateIndex) => {
            const candidatePath = `${path}.candidates[${candidateIndex}]`;
            const operation = requireRecord(
                requireRecord(candidate, candidatePath).operation,
                `${candidatePath}.operation`,
            );
            if (!Object.prototype.hasOwnProperty.call(operation, "alternative")) {
                return;
            }
            const alternative = requireRecord(
                operation.alternative,
                `${candidatePath}.operation.alternative`,
            );
            const value = Object.freeze({
                alternativeId: requireString(
                    alternative,
                    "alternativeId",
                    `${candidatePath}.operation.alternative`,
                ),
                familyId: requireString(
                    alternative,
                    "familyId",
                    `${candidatePath}.operation.alternative`,
                ),
            });
            if (
                resolved !== null
                && (
                    resolved.familyId !== value.familyId
                    || resolved.alternativeId !== value.alternativeId
                )
            ) {
                throw new CalculatorPresentationContractError(
                    `${path}.candidates`,
                    "trace contains conflicting rate alternatives",
                );
            }
            resolved = value;
        });
    return resolved;
}

function rateAlternativeTraceState(trace, path, resolutionByFamily) {
    const alternative = traceRateAlternative(trace, path);
    if (alternative === null) {
        return "active";
    }
    const resolution = resolutionByFamily.get(alternative.familyId);
    if (!resolution) {
        throw new CalculatorPresentationContractError(
            `${path}.candidates`,
            `rate-alternative family ${alternative.familyId} has no engine resolution`,
        );
    }
    if (resolution.alternativeId === alternative.alternativeId) {
        return "active";
    }
    return resolution.kind === "selectionRequired" ? "unresolved" : "inactive";
}

function rateAlternativeResolutionCacheKey(resolutionByFamily) {
    return [...resolutionByFamily]
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([familyId, resolution]) => (
            `${familyId}:${resolution.kind}:${resolution.alternativeId ?? ""}`
        ))
        .join("|");
}

function provenanceSegmentsForRow(traceIdsValue, traceById, presentation, path) {
    const segments = [];
    const seenTraceIds = new Set();
    requirePresentationArray(traceIdsValue, `${path}.traceIds`).forEach((traceId, traceIndex) => {
        const id = requirePresentationString(traceId, `${path}.traceIds[${traceIndex}]`);
        if (seenTraceIds.has(id)) {
            return;
        }
        seenTraceIds.add(id);
        const trace = traceById.get(id);
        if (!trace) {
            throw new CalculatorPresentationContractError(
                `${path}.traceIds[${traceIndex}]`,
                "unknown trace ID",
            );
        }
        const factKeyPath = `projection.provenanceTraces.${id}.factKey`;
        const factKey = requireRecord(trace.factKey, factKeyPath);
        const factKind = requireString(factKey, "kind", factKeyPath);
        const factLabel = calculatorProvenanceFactKindLabel(
            presentation.factKinds,
            factKind,
        );
        requirePresentationArray(
            trace.candidates,
            `projection.provenanceTraces.${id}.candidates`,
        ).forEach((candidate, candidateIndex) => {
            const candidatePath =
                `projection.provenanceTraces.${id}.candidates[${candidateIndex}]`;
            segments.push(projectCalculatorProvenanceCandidate(
                id,
                factKey,
                factLabel,
                candidate,
                candidatePath,
                presentation,
                presentation.factPresenter,
            ).segment);
        });
    });
    return Object.freeze(segments);
}

function traceCandidatePresentations(trace, traceId, presentation, path) {
    const factKey = requireRecord(trace.factKey, `${path}.factKey`);
    const factKind = requireString(factKey, "kind", `${path}.factKey`);
    const factLabel = calculatorProvenanceFactKindLabel(
        presentation.factKinds,
        factKind,
    );
    const candidates = requirePresentationArray(trace.candidates, `${path}.candidates`)
        .map((candidate, candidateIndex) => {
            const candidatePath = `${path}.candidates[${candidateIndex}]`;
            return projectCalculatorProvenanceCandidate(
                traceId,
                factKey,
                factLabel,
                candidate,
                candidatePath,
                presentation,
                presentation.factPresenter,
            );
        });
    return Object.freeze({
        factKey,
        factKind,
        candidates: Object.freeze(candidates),
    });
}

function orderedProvenanceTrace(trace, traceId, presentation, path) {
    const {
        factKey,
        factKind,
        candidates: projectedCandidates,
    } = traceCandidatePresentations(
        trace,
        traceId,
        presentation,
        path,
    );
    if (!Object.prototype.hasOwnProperty.call(trace, "effectiveFactId")) {
        throw new CalculatorPresentationContractError(
            `${path}.effectiveFactId`,
            "missing field",
        );
    }
    const effectiveFactId = requirePresentationNullableString(
        trace.effectiveFactId,
        `${path}.effectiveFactId`,
    );
    const rawCandidates = requirePresentationArray(
        trace.candidates,
        `${path}.candidates`,
    );
    const candidates = projectedCandidates.map((projected, candidateIndex) => {
        const candidatePath = `${path}.candidates[${candidateIndex}]`;
        const candidate = requireRecord(rawCandidates[candidateIndex], candidatePath);
        const deactivators = Object.prototype.hasOwnProperty.call(candidate, "deactivators")
            ? requirePresentationArray(
                candidate.deactivators,
                `${candidatePath}.deactivators`,
            )
            : [];
        return Object.freeze({
            candidateIndex,
            card: projected.card,
            deactivators: Object.freeze(deactivators.slice()),
            layer: requireRecord(candidate.layer, `${candidatePath}.layer`),
            operation: requireRecord(candidate.operation, `${candidatePath}.operation`),
            segment: projected.segment,
            status: projected.status,
        });
    });
    const effectiveCandidates = candidates.filter(({ status }) => status === "effective");
    if (effectiveFactId === null) {
        if (effectiveCandidates.length !== 0) {
            throw new CalculatorPresentationContractError(
                `${path}.candidates`,
                "trace without an effectiveFactId has effective candidates",
            );
        }
    } else {
        if (effectiveCandidates.length === 0) {
            throw new CalculatorPresentationContractError(
                `${path}.candidates`,
                "trace with an effectiveFactId has no effective candidate",
            );
        }
        if (effectiveCandidates.length > 1) {
            throw new CalculatorPresentationContractError(
                `${path}.candidates`,
                "trace has multiple effective candidates",
            );
        }
        const effectiveCandidate = effectiveCandidates[0];
        const effectiveCandidateFactId = requireString(
            effectiveCandidate.operation,
            "factId",
            `${path}.candidates[${effectiveCandidate.candidateIndex}].operation`,
        );
        if (effectiveCandidateFactId !== effectiveFactId) {
            throw new CalculatorPresentationContractError(
                `${path}.effectiveFactId`,
                "does not match the effective candidate factId",
            );
        }
    }
    return Object.freeze({
        traceId,
        factKey,
        factKind,
        effectiveFactId,
        candidates: Object.freeze(candidates),
    });
}

function effectiveProvenanceResolution(trace, traceId, presentation, path) {
    const { candidates } = traceCandidatePresentations(
        trace,
        traceId,
        presentation,
        path,
    );
    const effectiveCandidates = candidates.filter(({ status }) => status === "effective");
    if (effectiveCandidates.length > 1) {
        throw new CalculatorPresentationContractError(
            `${path}.candidates`,
            "semantic provenance trace has multiple effective candidates",
        );
    }
    const effective = effectiveCandidates[0] ?? null;
    if (!effective) {
        throw new CalculatorPresentationContractError(
            `${path}.candidates`,
            "semantic provenance trace has no effective candidate",
        );
    }
    const entries = calculatorProvenanceClaimEntries(candidates);
    const claims = mergeCalculatorProvenanceClaims([
        { claimEntries: entries },
    ]);
    return Object.freeze({
        claimEntries: entries,
        operationId: effective.operationId,
        segment: Object.freeze({
            ...effective.segment,
            claims,
            detail: provenanceClaimsText(claims),
        }),
        semanticKey: effective.semanticKey,
    });
}

function catalogItemPresenceProvenanceSegment(
    effectiveTraceId,
    traceIdsValue,
    traceById,
    presentation,
    itemId,
    path,
) {
    const effectiveId = requirePresentationString(
        effectiveTraceId,
        `${path}.effectiveTraceId`,
    );
    const matches = [];
    const seenTraceIds = new Set();
    requirePresentationArray(traceIdsValue, `${path}.traceIds`).forEach((traceId, index) => {
        const id = requirePresentationString(traceId, `${path}.traceIds[${index}]`);
        if (seenTraceIds.has(id)) {
            return;
        }
        seenTraceIds.add(id);
        const trace = traceById.get(id);
        if (!trace) {
            throw new CalculatorPresentationContractError(
                `${path}.traceIds[${index}]`,
                "unknown trace ID",
            );
        }
        const tracePath = `projection.provenanceTraces.${id}`;
        const factKey = requireRecord(trace.factKey, `${tracePath}.factKey`);
        const factKind = requireString(factKey, "kind", `${tracePath}.factKey`);
        if (factKind !== "itemPresence") {
            return;
        }
        if (requirePositiveInteger(factKey.itemId, `${tracePath}.factKey.itemId`) !== itemId) {
            return;
        }
        matches.push({
            id,
            trace,
            claimEntries: calculatorProvenanceClaimEntries(
                traceCandidatePresentations(
                    trace,
                    id,
                    presentation,
                    tracePath,
                ).candidates,
            ),
        });
    });
    const effectiveMatch = matches.find(({ id }) => id === effectiveId);
    if (!effectiveMatch) {
        throw new CalculatorPresentationContractError(
            `${path}.effectiveTraceId`,
            `does not identify itemPresence for item ${itemId}`,
        );
    }
    const effective = effectiveProvenanceResolution(
        effectiveMatch.trace,
        effectiveMatch.id,
        presentation,
        `projection.provenanceTraces.${effectiveMatch.id}`,
    );
    const claims = mergeCalculatorProvenanceClaims(matches.filter(({ claimEntries }) => (
        claimEntries.some(({ status }) => status === "effective")
    )));
    return Object.freeze({
        ...effective.segment,
        claims,
        detail: provenanceClaimsText(claims),
    });
}

function effectiveProvenanceSegment(trace, traceId, presentation, path) {
    return effectiveProvenanceResolution(trace, traceId, presentation, path).segment;
}

function additivePrizeRateResolutionOrder(left, right) {
    const rank = (factKind) => (factKind === "groupRate" ? 0 : 1);
    return rank(left.factKind) - rank(right.factKind)
        || left.segment.segmentId.localeCompare(right.segment.segmentId);
}

function combinedRateSegment(
    resolutions,
    claims,
    { active, detail, factKind, label, segmentId },
) {
    const representative = resolutions[0].segment;
    const sharedTone = resolutions.every(({ segment }) => (
        segment.sourceTone === representative.sourceTone
    ));
    const sharedSource = resolutions.every(({ segment }) => (
        segment.sourceKind === representative.sourceKind
        && segment.sourceLabel === representative.sourceLabel
    ));
    const style = sharedTone
        ? {
            color: active
                ? representative.color
                : provenanceIndicatorColorForTone(representative.sourceTone, { active }),
            sourceTone: representative.sourceTone,
        }
        : typedProvenanceIndicatorStyle(factKind, "mixed", { active });
    return Object.freeze({
        segmentId,
        active,
        color: style.color,
        claims,
        detail,
        label,
        sortRank: Math.max(...resolutions.map(({ segment }) => segment.sortRank)),
        sourceIcon: sharedSource ? representative.sourceIcon : "",
        sourceKind: sharedSource ? representative.sourceKind : "mixed",
        sourceLabel: sharedSource
            ? representative.sourceLabel
            : calculatorDataText(
                "common.provenance.source.mixed_sources",
                "Mixed sources",
            ),
        sourceTone: style.sourceTone,
    });
}

function additivePrizeRateSegment(resolutions, claims, presentation, groupId) {
    return combinedRateSegment(resolutions, claims, {
        active: true,
        detail: provenanceClaimsText(claims),
        factKind: "groupRate",
        label: calculatorProvenanceFactKindLabel(
            presentation.factKinds,
            "groupRate",
        ),
        segmentId: `semantic:prize-rate:${groupId}:${resolutions
            .map(({ segment }) => segment.segmentId)
            .join("+")}`,
    });
}

function itemRateWithQuantitySegment(
    rateSegment,
    quantitySegment,
    claims,
    itemId,
    groupId,
    detailPrefix = "",
) {
    if (quantitySegment === null) {
        return Object.freeze({
            ...rateSegment,
            quantityProvenanceRelevant: false,
        });
    }
    return Object.freeze({
        ...rateSegment,
        segmentId: `semantic:item-rate:${groupId}:${itemId}:${rateSegment.segmentId ?? "missing"}+${quantitySegment.segmentId}`,
        claims,
        detail: `${detailPrefix}${detailPrefix ? " · " : ""}${provenanceClaimsText(claims)}`,
        quantityProvenanceRelevant: true,
    });
}

function inactiveItemRateSegment(
    resolutions,
    claims,
    inactiveConditionLabel,
    itemId,
    groupId,
) {
    return Object.freeze({
        ...combinedRateSegment(resolutions, claims, {
            active: false,
            detail: `${inactiveConditionLabel} · ${provenanceClaimsText(claims)}`,
            factKind: "itemRate",
            label: resolutions[0].segment.label,
            segmentId: `semantic:inactive-item-rate:${groupId}:${itemId}:${resolutions
                .map(({ segment }) => segment.segmentId)
                .join("+")}`,
        }),
        channel: "rate",
    });
}

function semanticGroupProvenanceSegments(
    traceIdsValue,
    traceById,
    presentation,
    conditionsByOperationId,
    rateAlternativeResolutionByFamily,
    groupId,
    groupRole,
    path,
) {
    const matches = new Map();
    const subject = Object.freeze({ kind: "group", groupId });
    requirePresentationArray(traceIdsValue, `${path}.traceIds`).forEach((traceId, traceIndex) => {
        const tracePath = `${path}.traceIds[${traceIndex}]`;
        const id = requirePresentationString(traceId, tracePath);
        const trace = traceById.get(id);
        if (!trace) {
            throw new CalculatorPresentationContractError(tracePath, "unknown trace ID");
        }
        const factKeyPath = `projection.provenanceTraces.${id}.factKey`;
        const factKey = requireRecord(trace.factKey, factKeyPath);
        const factKind = requireString(factKey, "kind", factKeyPath);
        const semanticKind = factKind === "masteryPrizeRate" ? "groupRate" : factKind;
        if (semanticKind !== "groupPresence" && semanticKind !== "groupRate") {
            return;
        }
        if (factKind === "masteryPrizeRate") {
            if (groupRole !== "prize") {
                return;
            }
        } else {
            if (requireString(factKey, "groupId", factKeyPath) !== groupId) {
                return;
            }
            requireFishingSource(factKey, "fishingSource", factKeyPath);
            if (calculatorLootSubjectFactChannel(factKey, subject) === null) {
                return;
            }
        }
        if (factKind === "groupRate") {
            if (!Object.prototype.hasOwnProperty.call(factKey, "branchId")) {
                throw new CalculatorPresentationContractError(
                    `${factKeyPath}.branchId`,
                    "missing field",
                );
            }
            const branchId = requirePresentationNullableString(
                factKey.branchId,
                `${factKeyPath}.branchId`,
            );
            // Branch-scoped GroupRate facts select a subgroup/item-rate
            // alternative. They remain visible in the detailed group graph,
            // but they are not a numeric input to the top-level group rate.
            if (branchId !== null) {
                return;
            }
            if (rateAlternativeTraceState(
                trace,
                `projection.provenanceTraces.${id}`,
                rateAlternativeResolutionByFamily,
            ) !== "active") {
                return;
            }
        }
        const kindMatches = matches.get(semanticKind) ?? [];
        kindMatches.push({ factKind, id, trace });
        matches.set(semanticKind, kindMatches);
    });
    let conditionText = "";
    const segments = ["groupPresence", "groupRate"].map((kind) => {
        const kindMatches = matches.get(kind);
        if (!kindMatches?.length) {
            throw new CalculatorPresentationContractError(
                `${path}.traceIds`,
                `missing ${kind} trace for group ${groupId}`,
            );
        }
        let resolutions = kindMatches.map((match) => ({
            ...effectiveProvenanceResolution(
                match.trace,
                match.id,
                presentation,
                `projection.provenanceTraces.${match.id}`,
            ),
            factKind: match.factKind,
        }));
        const additivePrizeRate = kind === "groupRate"
            && groupRole === "prize"
            && resolutions.some(({ factKind }) => factKind === "groupRate")
            && resolutions.some(({ factKind }) => factKind === "masteryPrizeRate")
            && ["groupRate", "masteryPrizeRate"].every((factKind) => {
                const matching = resolutions.filter((resolution) => (
                    resolution.factKind === factKind
                ));
                return matching.every((resolution) => (
                    resolution.semanticKey === matching[0].semanticKey
                ));
            });
        resolutions = resolutions.sort(additivePrizeRate
            ? additivePrizeRateResolutionOrder
            : (left, right) => left.segment.segmentId.localeCompare(
                right.segment.segmentId,
            ));
        const semanticKey = resolutions[0].semanticKey;
        if (
            !additivePrizeRate
            && resolutions.some((resolution) => resolution.semanticKey !== semanticKey)
        ) {
            throw new CalculatorPresentationContractError(
                `${path}.traceIds`,
                `conflicting effective ${kind} sources for group ${groupId}`,
            );
        }
        if (kind === "groupRate") {
            const conditionTexts = [...new Set(resolutions
                .filter(({ factKind }) => factKind === "groupRate")
                .map(({ operationId }) => conditionsByOperationId[operationId] ?? "")
                .filter(Boolean))];
            if (conditionTexts.length > 1) {
                throw new CalculatorPresentationContractError(
                    `${path}.traceIds`,
                    `conflicting effective groupRate conditions for group ${groupId}`,
                );
            }
            conditionText = conditionTexts[0] ?? "";
        }
        const claims = mergeCalculatorProvenanceClaims(resolutions);
        const segment = additivePrizeRate
            ? additivePrizeRateSegment(
                resolutions,
                claims,
                presentation,
                groupId,
            )
            : resolutions[0].segment;
        return Object.freeze({
            ...segment,
            channel: kind === "groupPresence" ? "presence" : "rate",
            claims,
            detail: provenanceClaimsText(claims),
        });
    });
    return Object.freeze({
        conditionText,
        segments: Object.freeze(segments),
    });
}

function semanticItemProvenanceSegments(
    traceIdsValue,
    traceById,
    presentation,
    rateAlternativeResolutionByFamily,
    itemId,
    groupId,
    path,
    { knownZeroRate = false, representMissingItemRate = false } = {},
) {
    const matches = new Map();
    const inactiveRateMatches = [];
    let hasUnresolvedRate = false;
    const subject = Object.freeze({ kind: "row", groupId, itemId });
    requirePresentationArray(traceIdsValue, `${path}.traceIds`).forEach((traceId, traceIndex) => {
        const tracePath = `${path}.traceIds[${traceIndex}]`;
        const id = requirePresentationString(traceId, tracePath);
        const trace = traceById.get(id);
        if (!trace) {
            throw new CalculatorPresentationContractError(tracePath, "unknown trace ID");
        }
        const factKeyPath = `projection.provenanceTraces.${id}.factKey`;
        const factKey = requireRecord(trace.factKey, factKeyPath);
        const kind = requireString(factKey, "kind", factKeyPath);
        if (
            kind !== "itemPresence"
            && kind !== "itemRate"
            && kind !== "itemQuantity"
            && kind !== "itemQuantityDefault"
            && kind !== "groupMembership"
        ) {
            return;
        }
        if (requirePositiveInteger(factKey.itemId, `${factKeyPath}.itemId`) !== itemId) {
            return;
        }
        const semanticKind = kind === "itemQuantityDefault"
            ? "itemQuantity"
            : kind;
        if (
            (semanticKind === "itemRate"
                || (semanticKind === "itemQuantity" && kind !== "itemQuantityDefault")
                || semanticKind === "groupMembership")
            && requireString(factKey, "groupId", factKeyPath) !== groupId
        ) {
            return;
        }
        if (kind !== "itemQuantityDefault") {
            requireFishingSource(factKey, "fishingSource", factKeyPath);
        }
        if (calculatorLootSubjectFactChannel(factKey, subject) === null) {
            return;
        }
        if (kind === "itemRate") {
            const alternativeState = rateAlternativeTraceState(
                trace,
                `projection.provenanceTraces.${id}`,
                rateAlternativeResolutionByFamily,
            );
            if (alternativeState !== "active") {
                if (alternativeState === "inactive") {
                    inactiveRateMatches.push({ id, trace });
                } else {
                    hasUnresolvedRate = true;
                }
                return;
            }
        }
        if (!matches.has(semanticKind)) {
            matches.set(semanticKind, []);
        }
        matches.get(semanticKind).push({ id, trace });
    });
    const representsInactiveRate = !(matches.get("itemRate")?.length)
        && inactiveRateMatches.length > 0
        && !hasUnresolvedRate
        && knownZeroRate === true;
    if (representsInactiveRate) {
        // A present item can have a known-zero rate because every authored
        // branch that contains it is inactive for the selected alternative.
        // Keep those engine-cited facts distinct from a genuinely missing rate.
        matches.set("itemRate", inactiveRateMatches);
    }
    const inactiveConditionLabel = representsInactiveRate
        ? requirePresentationString(
            presentation.statuses.inactiveCondition,
            "presentation.provenance.statuses.inactiveCondition",
        )
        : "";
    const quantityProvenanceRelevant = (matches.get("itemQuantity") ?? [])
        .map((match) => {
            const tracePath = `projection.provenanceTraces.${match.id}`;
            traceCandidatePresentations(
                match.trace,
                match.id,
                presentation,
                tracePath,
            );
            return calculatorItemQuantityHistoryIsPresentationRelevant(
                match.trace.candidates,
                `${tracePath}.candidates`,
            );
        })
        .some(Boolean);
    const kinds = ["itemPresence", "itemRate"];
    if (quantityProvenanceRelevant) {
        kinds.push("itemQuantity");
    }
    const segmentsByKind = new Map(kinds.map((kind) => {
        const kindMatches = matches.get(kind);
        if (!kindMatches?.length) {
            if (kind === "itemRate" && representMissingItemRate) {
                const style = typedProvenanceIndicatorStyle(
                    "itemRate",
                    "unknown",
                    { active: false },
                );
                return [kind, Object.freeze({
                    active: false,
                    channel: "rate",
                    color: style.color,
                    detail: "",
                    claims: Object.freeze([]),
                    label: calculatorProvenanceFactKindLabel(
                        presentation.factKinds,
                        "itemRate",
                    ),
                    sortRank: 0,
                    sourceIcon: style.sourceIcon,
                    sourceKind: "unknown",
                    sourceLabel: presentation.unknownValueLabel,
                    sourceTone: style.sourceTone,
                })];
            }
            throw new CalculatorPresentationContractError(
                `${path}.traceIds`,
                `missing ${kind} trace for item ${itemId} in group ${groupId}`,
            );
        }
        const candidateCollections = kindMatches.map((match) => {
            const tracePath = `projection.provenanceTraces.${match.id}`;
            const candidates = traceCandidatePresentations(
                match.trace,
                match.id,
                presentation,
                tracePath,
            ).candidates;
            return {
                ...match,
                candidates,
                claimEntries: calculatorProvenanceClaimEntries(candidates),
            };
        });
        const effectiveMatches = candidateCollections.filter(({ candidates }) => (
            candidates.some(({ status }) => status === "effective")
        ));
        if (!effectiveMatches.length) {
            if (kind === "itemQuantity") {
                return [kind, null];
            }
            throw new CalculatorPresentationContractError(
                `${path}.traceIds`,
                `no effective ${kind} trace for item ${itemId} in group ${groupId}`,
            );
        }
        const resolutions = effectiveMatches.map((match) => effectiveProvenanceResolution(
            match.trace,
            match.id,
            presentation,
            `projection.provenanceTraces.${match.id}`,
        )).sort((left, right) => (
            left.segment.segmentId < right.segment.segmentId
                ? -1
                : (left.segment.segmentId > right.segment.segmentId ? 1 : 0)
        ));
        if (kind === "itemRate" && representsInactiveRate) {
            const claims = mergeCalculatorProvenanceClaims(resolutions);
            return [kind, inactiveItemRateSegment(
                resolutions,
                claims,
                inactiveConditionLabel,
                itemId,
                groupId,
            )];
        }
        const semanticKey = resolutions[0].semanticKey;
        if (resolutions.some((resolution) => resolution.semanticKey !== semanticKey)) {
            throw new CalculatorPresentationContractError(
                `${path}.traceIds`,
                `conflicting effective ${kind} sources for item ${itemId} in group ${groupId}`,
            );
        }
        const membershipResolutions = kind === "itemPresence"
            ? (matches.get("groupMembership") ?? []).map((match) => (
                effectiveProvenanceResolution(
                    match.trace,
                    match.id,
                    presentation,
                    `projection.provenanceTraces.${match.id}`,
                )
            )).sort((left, right) => (
                left.segment.segmentId.localeCompare(right.segment.segmentId)
            ))
            : [];
        const claims = mergeCalculatorProvenanceClaims([
            ...resolutions,
            ...membershipResolutions,
        ]);
        return [kind, Object.freeze({
            ...resolutions[0].segment,
            channel: kind === "itemPresence" ? "presence" : "rate",
            claims,
            detail: provenanceClaimsText(claims),
        })];
    }).filter(([, segment]) => segment !== null));
    const presenceSegment = segmentsByKind.get("itemPresence");
    const rateSegment = segmentsByKind.get("itemRate");
    const quantitySegment = segmentsByKind.get("itemQuantity") ?? null;
    const rateClaims = quantitySegment === null
        ? rateSegment.claims
        : Object.freeze([
            ...rateSegment.claims,
            ...quantitySegment.claims,
        ]);
    return Object.freeze([
        presenceSegment,
        itemRateWithQuantitySegment(
            rateSegment,
            quantitySegment,
            rateClaims,
            itemId,
            groupId,
            inactiveConditionLabel,
        ),
    ]);
}

function provenanceTraceStateCacheKey(
    traceIdsValue,
    traceById,
    cache,
    path,
) {
    return requirePresentationArray(traceIdsValue, `${path}.traceIds`)
        .map((traceId, traceIndex) => {
            const id = requirePresentationString(
                traceId,
                `${path}.traceIds[${traceIndex}]`,
            );
            const trace = traceById.get(id);
            if (!trace) {
                throw new CalculatorPresentationContractError(
                    `${path}.traceIds[${traceIndex}]`,
                    "unknown trace ID",
                );
            }
            const candidates = requirePresentationArray(
                trace.candidates,
                `projection.provenanceTraces.${id}.candidates`,
            ).map((candidate, candidateIndex) => {
                const candidatePath =
                    `projection.provenanceTraces.${id}.candidates[${candidateIndex}]`;
                const value = requireRecord(candidate, candidatePath);
                const layer = requireRecord(value.layer, `${candidatePath}.layer`);
                const operation = requireRecord(value.operation, `${candidatePath}.operation`);
                const status = requireString(value, "status", candidatePath);
                return `${cache.objectId(layer)}:${cache.objectId(operation)}:${status}`;
            });
            return `${id}[${candidates.join(",")}]`;
        })
        .join("|");
}

function cachedSemanticResolution(cache, key, resolve) {
    if (cache === null) {
        return resolve();
    }
    if (cache.has(key)) {
        return cache.get(key);
    }
    return cache.set(key, resolve());
}

/**
 * Builds one revision-bound resolver for compact provenance indicators and
 * their complete typed detail cards.
 */
export function createCalculatorProvenanceSegmentResolver(
    projection,
    presentationValue,
    { cache = null } = {},
) {
    const parsed = parseResolverPresentation(presentationValue);
    const inspectorPresentation = Object.freeze({
        ...parsed.provenance,
        factPresenter: parsed.factPresenter,
        unknownValueLabel: parsed.unknownValueLabel,
    });
    const traceById = provenanceTraceMap(projection?.provenanceTraces);
    const rateAlternativeResolutionByFamily = rateAlternativeResolutionMap(
        projection?.rateAlternativeResolutions,
    );
    const rateAlternativeCacheKey = rateAlternativeResolutionCacheKey(
        rateAlternativeResolutionByFamily,
    );
    return Object.freeze({
        resolve(traceIds, path = "projection.provenanceTraces") {
            return provenanceSegmentsForRow(
                traceIds,
                traceById,
                inspectorPresentation,
                path,
            );
        },
        resolveEffective(traceId, path = "projection.provenanceTraces") {
            const id = requirePresentationString(traceId, `${path}.traceId`);
            const trace = traceById.get(id);
            if (!trace) {
                throw new CalculatorPresentationContractError(
                    `${path}.traceId`,
                    "unknown trace ID",
                );
            }
            return effectiveProvenanceSegment(
                trace,
                id,
                inspectorPresentation,
                `projection.provenanceTraces.${id}`,
            );
        },
        resolveOrderedTrace(traceId, path = "projection.provenanceTraces") {
            const id = requirePresentationString(traceId, `${path}.traceId`);
            const trace = traceById.get(id);
            if (!trace) {
                throw new CalculatorPresentationContractError(
                    `${path}.traceId`,
                    "unknown trace ID",
                );
            }
            return orderedProvenanceTrace(
                trace,
                id,
                inspectorPresentation,
                `projection.provenanceTraces.${id}`,
            );
        },
        resolveCatalogItemPresence(
            effectiveTraceId,
            traceIds,
            itemId,
            path = "lootEditor.catalog",
        ) {
            return catalogItemPresenceProvenanceSegment(
                effectiveTraceId,
                traceIds,
                traceById,
                inspectorPresentation,
                requirePositiveInteger(itemId, `${path}.itemId`),
                path,
            );
        },
        resolveSemanticGroup(
            traceIds,
            groupId,
            groupRole,
            path = "projection.sankey.groups",
        ) {
            const parsedGroupId = requirePresentationString(groupId, `${path}.groupId`);
            const parsedGroupRole = requirePresentationString(groupRole, `${path}.role`);
            if (cache === null) {
                return semanticGroupProvenanceSegments(
                    traceIds,
                    traceById,
                    inspectorPresentation,
                    parsed.conditionsByOperationId,
                    rateAlternativeResolutionByFamily,
                    parsedGroupId,
                    parsedGroupRole,
                    path,
                );
            }
            const traceStateKey = provenanceTraceStateCacheKey(
                traceIds,
                traceById,
                cache,
                path,
            );
            return cachedSemanticResolution(
                cache,
                `group\u0000${parsedGroupId}\u0000${parsedGroupRole}\u0000${rateAlternativeCacheKey}\u0000${traceStateKey}`,
                () => semanticGroupProvenanceSegments(
                    traceIds,
                    traceById,
                    inspectorPresentation,
                    parsed.conditionsByOperationId,
                    rateAlternativeResolutionByFamily,
                    parsedGroupId,
                    parsedGroupRole,
                    path,
                ),
            );
        },
        resolveSemanticItem(
            traceIds,
            itemId,
            groupId,
            path = "projection.sankey.items",
            { knownZeroRate = false } = {},
        ) {
            const parsedItemId = requirePositiveInteger(itemId, `${path}.itemId`);
            const parsedGroupId = requirePresentationString(groupId, `${path}.groupId`);
            if (cache === null) {
                return semanticItemProvenanceSegments(
                    traceIds,
                    traceById,
                    inspectorPresentation,
                    rateAlternativeResolutionByFamily,
                    parsedItemId,
                    parsedGroupId,
                    path,
                    { knownZeroRate },
                );
            }
            const traceStateKey = provenanceTraceStateCacheKey(
                traceIds,
                traceById,
                cache,
                path,
            );
            return cachedSemanticResolution(
                cache,
                `item\u0000${parsedGroupId}\u0000${parsedItemId}\u0000${knownZeroRate === true ? "zero" : "nonzero"}\u0000${rateAlternativeCacheKey}\u0000${traceStateKey}`,
                () => semanticItemProvenanceSegments(
                    traceIds,
                    traceById,
                    inspectorPresentation,
                    rateAlternativeResolutionByFamily,
                    parsedItemId,
                    parsedGroupId,
                    path,
                    { knownZeroRate },
                ),
            );
        },
        resolveInspectorItem(
            traceIds,
            itemId,
            groupId,
            path = "projection.unquantifiedItems",
        ) {
            return semanticItemProvenanceSegments(
                traceIds,
                traceById,
                inspectorPresentation,
                rateAlternativeResolutionByFamily,
                requirePositiveInteger(itemId, `${path}.itemId`),
                requirePresentationString(groupId, `${path}.groupId`),
                path,
                { representMissingItemRate: true },
            );
        },
    });
}
